import { useEffect, useState } from "react";
import { getDemoProductionRegistration, getRegistrationConfiguration, submitDemoProductionRegistration, submitRegistration } from "../api/registration";
import AsyncButton from "../components/AsyncButton";
import FieldError from "../components/FieldError";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage, getFieldError } from "../utils/apiErrors";
import { Link } from "react-router-dom";
import PublicFooter from "../components/PublicFooter";
import PageMetadata from "../components/PageMetadata";

export default function Register() {
  const demoBridge = new URLSearchParams(window.location.search).get('source') === 'demo';
  const [form, setForm] = useState({
    type: new URLSearchParams(window.location.search).get('type') === 'attorney' ? 'attorney' : 'facility',
    organization_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    bar_number: "",
    notes: "",
    website: "",
    public_facility_name: "",
    facility_type: "",
    physical_address: "",
    city: "",
    state: "",
    postal_code: "",
    main_phone: "",
    official_website: "",
    applicant_title: "",
    estimated_population: "",
    requested_administrator_role: "facility_admin",
    customer_reference: "",
    authority_certified: false,
    terms_accepted: false,
    privacy_accepted: false,
  });
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    getRegistrationConfiguration().then((response) => setConfiguration(response.data)).catch(() => setError('Unable to load current registration agreements.'));
    if (demoBridge) getDemoProductionRegistration().then((response) => {
      if (response.data) {
        setForm(prev => ({ ...prev, ...response.data, contact_email: response.identity.email, contact_name: response.identity.name }));
        setSuccess('Your existing production registration has been resumed.');
      } else setForm(prev => ({ ...prev, contact_email: response.identity.email, contact_name: response.identity.name }));
    }).catch(() => setError('Production setup is available only from an active demo workspace.'));
  }, [demoBridge]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    setFieldErrors({});

    try {
      await (demoBridge ? submitDemoProductionRegistration(form) : submitRegistration(form));
	setSuccess(demoBridge ? "Production registration submitted for review. You remain in the training workspace until final activation." :
	  form.type === "attorney"
	    ? "Attorney registration received. Check your email for account setup instructions."
	    : "Facility registration request submitted. CounselVault will review and send setup instructions after approval."
	);
      if (demoBridge) return;
      setForm({
        type: form.type,
        organization_name: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        bar_number: "",
        notes: "",
	website: "",
        public_facility_name: "", facility_type: "", physical_address: "", city: "", state: "", postal_code: "",
        main_phone: "", official_website: "", applicant_title: "", estimated_population: "",
        requested_administrator_role: "facility_admin", customer_reference: "", authority_certified: false,
        terms_accepted: false, privacy_accepted: false,
      });
    } catch (err) {
      setError(getErrorMessage(err, "Unable to submit registration request."));
      setFieldErrors(err.fieldErrors || {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <PageMetadata title="Request CounselVault Access" description="Apply for verified facility access or register an attorney account with CounselVault." path="/register" />
      <div className="login-card" style={{ maxWidth: 760 }}>
        {demoBridge && <div className="alert alert-warning"><strong>Demo-to-production setup</strong> — your demo remains synthetic and active until production approval. No demo records will be copied.</div>}
        {demoBridge && <div className="alert alert-info">Production activation requires recent MFA verification. <a href={`/mfa/setup?production_setup=1&return=${encodeURIComponent(window.location.pathname + window.location.search)}`}>Enroll or verify MFA</a> before platform approval.</div>}
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="brand-mark">CV</div>
          <div>
            <h4 className="mb-0">CounselVault Registration</h4>
            <div className="text-muted small">Secure privileged legal mail infrastructure</div>
          </div>
        </div>

        <h2 className="fw-bold mb-2">Request Access</h2>
        <p className="text-muted mb-4">
          Registration is reviewed by CounselVault before any account is activated.
        </p>

        <StatusMessage type="warning">{error}</StatusMessage>
        <StatusMessage type="success">{success}</StatusMessage>

        <form onSubmit={handleSubmit}>
	<input
	  type="text"
	  name="website"
	  value={form.website}
	  onChange={(e) => update("website", e.target.value)}
	  tabIndex="-1"
	  autoComplete="off"
	  aria-hidden="true"
	  style={{
	    position: "absolute",
	    left: "-9999px",
	    opacity: 0,
	    pointerEvents: "none",
	  }}
	/>
          <div className="mb-3">
            <label className="form-label" htmlFor="registration-type">Register as</label>
            <select id="registration-type" className="form-select form-select-lg" value={form.type} onChange={(e) => update("type", e.target.value)}>
              <option value="facility">Correctional Facility / Jail</option>
              <option value="attorney">Attorney / Law Firm</option>
            </select>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="organization-name">{form.type === "facility" ? "Facility Name" : "Law Firm Name"}</label>
              <input id="organization-name" className="form-control form-control-lg" value={form.organization_name} onChange={(e) => update("organization_name", e.target.value)} aria-invalid={!!getFieldError({ fieldErrors }, "organization_name")} aria-describedby="organization-name-error" required />
              <FieldError id="organization-name-error">{getFieldError({ fieldErrors }, "organization_name")}</FieldError>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="contact-name">Contact Name</label>
              <input id="contact-name" className="form-control form-control-lg" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} aria-invalid={!!getFieldError({ fieldErrors }, "contact_name")} aria-describedby="contact-name-error" required />
              <FieldError id="contact-name-error">{getFieldError({ fieldErrors }, "contact_name")}</FieldError>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="contact-email">{form.type === 'facility' ? 'Applicant official work email' : 'Contact Email'}</label>
              <input id="contact-email" type="email" className="form-control form-control-lg" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} aria-invalid={!!getFieldError({ fieldErrors }, "contact_email")} aria-describedby="contact-email-error" required readOnly={demoBridge} />
              <FieldError id="contact-email-error">{getFieldError({ fieldErrors }, "contact_email")}</FieldError>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{form.type === 'facility' ? 'Applicant phone (optional)' : 'Phone'}</label>
              <input className="form-control form-control-lg" value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} />
            </div>
          </div>

          {form.type === "attorney" && (
            <><div className="mb-3">
              <label className="form-label" htmlFor="bar-number">Bar Number</label>
              <input id="bar-number" className="form-control form-control-lg" value={form.bar_number} onChange={(e) => update("bar_number", e.target.value)} required />
            </div><div className="border rounded p-3 mb-4" aria-label="Attorney agreements"><div className="form-check mb-2"><input id="attorney-terms-accepted" className="form-check-input" type="checkbox" checked={form.terms_accepted} onChange={(e) => update('terms_accepted', e.target.checked)} required /><label className="form-check-label" htmlFor="attorney-terms-accepted">I accept the <Link to="/legal/terms" target="_blank" rel="noreferrer">Terms of Service</Link>.</label></div><div className="form-check"><input id="attorney-privacy-accepted" className="form-check-input" type="checkbox" checked={form.privacy_accepted} onChange={(e) => update('privacy_accepted', e.target.checked)} required /><label className="form-check-label" htmlFor="attorney-privacy-accepted">I acknowledge the <Link to="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</Link>.</label></div></div></>
          )}

          {form.type === "facility" && (
            <>
              <h5 className="mt-4">Facility details</h5>
              <div className="row">
                <div className="col-md-6 mb-3"><label className="form-label">Public-facing name (if different)</label><input className="form-control" value={form.public_facility_name} onChange={(e) => update('public_facility_name', e.target.value)} maxLength="255" /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Facility type</label><select className="form-select" value={form.facility_type} onChange={(e) => update('facility_type', e.target.value)} required><option value="">Select facility type</option>{(configuration?.facility_types || []).map((value) => <option key={value} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></div>
              </div>
              <div className="mb-3"><label className="form-label">Physical address</label><input className="form-control" value={form.physical_address} onChange={(e) => update('physical_address', e.target.value)} required maxLength="255" /></div>
              <div className="row">
                <div className="col-md-5 mb-3"><label className="form-label">City</label><input className="form-control" value={form.city} onChange={(e) => update('city', e.target.value)} required maxLength="100" /></div>
                <div className="col-md-4 mb-3"><label className="form-label">State</label><input className="form-control" value={form.state} onChange={(e) => update('state', e.target.value)} required maxLength="100" /></div>
                <div className="col-md-3 mb-3"><label className="form-label">Postal code</label><input className="form-control" value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)} required maxLength="20" /></div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3"><label className="form-label">Main facility phone</label><input type="tel" className="form-control" value={form.main_phone} onChange={(e) => update('main_phone', e.target.value)} required maxLength="50" /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Official website (optional)</label><input type="url" className="form-control" value={form.official_website} onChange={(e) => update('official_website', e.target.value)} maxLength="255" placeholder="https://" /></div>
              </div>
              <h5 className="mt-3">Applicant authority</h5>
              <div className="row">
                <div className="col-md-6 mb-3"><label className="form-label">Applicant job title</label><input className="form-control" value={form.applicant_title} onChange={(e) => update('applicant_title', e.target.value)} required maxLength="255" /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Estimated population or capacity (optional)</label><input type="number" min="1" max="10000000" className="form-control" value={form.estimated_population} onChange={(e) => update('estimated_population', e.target.value)} /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Requested administrator role</label><select className="form-select" value={form.requested_administrator_role} onChange={(e) => update('requested_administrator_role', e.target.value)} required><option value="facility_admin">Facility administrator</option></select></div>
                <div className="col-md-6 mb-3"><label className="form-label">Internal/customer reference (optional)</label><input className="form-control" value={form.customer_reference} onChange={(e) => update('customer_reference', e.target.value)} maxLength="255" /></div>
              </div>
              <div className="border rounded p-3 mb-4">
                <div className="fw-semibold mb-2">Authority certification</div>
                <ul className="small">{(configuration?.authority_certifications || []).map((statement) => <li key={statement}>{statement}</li>)}</ul>
                <div className="form-check mb-2"><input id="authority-certified" className="form-check-input" type="checkbox" checked={form.authority_certified} onChange={(e) => update('authority_certified', e.target.checked)} required /><label className="form-check-label" htmlFor="authority-certified">I affirm and accept the <Link to="/legal/facility-authority" target="_blank" rel="noreferrer">Facility Authority Agreement</Link>.</label></div>
                <div className="form-check mb-2"><input id="terms-accepted" className="form-check-input" type="checkbox" checked={form.terms_accepted} onChange={(e) => update('terms_accepted', e.target.checked)} required /><label className="form-check-label" htmlFor="terms-accepted">I accept the <Link to="/legal/terms" target="_blank" rel="noreferrer">Terms of Service</Link>.</label></div>
                <div className="form-check"><input id="privacy-accepted" className="form-check-input" type="checkbox" checked={form.privacy_accepted} onChange={(e) => update('privacy_accepted', e.target.checked)} required /><label className="form-check-label" htmlFor="privacy-accepted">I acknowledge the <Link to="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</Link>.</label></div>
              </div>
            </>
          )}

          <div className="mb-4">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows="4" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Optional context for review" />
          </div>

          <AsyncButton className="btn btn-dark btn-lg w-100" busy={loading || (form.type === 'facility' && !configuration)} disabled={form.type === 'facility' && !configuration} busyText="Submitting…">Submit Registration Request</AsyncButton>
        </form>

        <div className="mt-4 text-center">
          <a href="/login">Back to sign in</a>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
